import React from 'react';
import { ACTIVITY_WINDOWS, LeaguePlayerFilters, hasActivePanelFilters } from '../utils/leaguePlayerFilters';

interface LeaguePlayerFilterPanelProps {
  filters: LeaguePlayerFilters;
  onChange: (filters: LeaguePlayerFilters) => void;
  /** Games that have a play in this league; hidden when empty or the league tracks one game */
  gameOptions: { id: string; name: string }[];
  /** False while plays are still loading: "Active in" and "Played game" can't be answered yet */
  activityReady: boolean;
}

const controlClass =
  'w-full min-h-[44px] px-3 py-2 text-base sm:text-sm border border-edge-input rounded-md bg-surface text-body focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50';

/** Filter panel for the league members list (shown under the "League Members" header). */
const LeaguePlayerFilterPanel: React.FC<LeaguePlayerFilterPanelProps> = ({
  filters,
  onChange,
  gameOptions,
  activityReady,
}) => {
  const update = (patch: Partial<LeaguePlayerFilters>) => onChange({ ...filters, ...patch });

  return (
    <div className="bg-surface-base px-4 sm:px-6 py-4 border-b border-edge" data-testid="player-filter-panel">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label htmlFor="player-filter-min-games" className="block text-sm font-medium text-heading mb-1">
            Minimum games
          </label>
          <input
            id="player-filter-min-games"
            data-track="league_filter_min_games"
            type="number"
            inputMode="numeric"
            min={0}
            step={1}
            placeholder="Any"
            value={filters.minGames > 0 ? String(filters.minGames) : ''}
            onChange={e => {
              const value = parseInt(e.target.value, 10);
              update({ minGames: Number.isFinite(value) && value > 0 ? value : 0 });
            }}
            className={controlClass}
          />
        </div>

        <div>
          <label htmlFor="player-filter-active-in" className="block text-sm font-medium text-heading mb-1">
            Active in
          </label>
          <select
            id="player-filter-active-in"
            data-track="league_filter_active_in"
            value={filters.activeWithinDays === null ? '' : String(filters.activeWithinDays)}
            disabled={!activityReady}
            onChange={e => update({ activeWithinDays: e.target.value ? Number(e.target.value) : null })}
            className={controlClass}
          >
            {ACTIVITY_WINDOWS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {gameOptions.length > 0 && (
          <div>
            <label htmlFor="player-filter-game" className="block text-sm font-medium text-heading mb-1">
              Played game
            </label>
            <select
              id="player-filter-game"
              data-track="league_filter_game"
              value={filters.gameId}
              disabled={!activityReady}
              onChange={e => update({ gameId: e.target.value })}
              className={controlClass}
            >
              <option value="">Any game</option>
              {gameOptions.map(game => (
                <option key={game.id} value={game.id}>
                  {game.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <label className="flex items-center cursor-pointer mt-4 min-h-[44px] sm:min-h-0">
        <input
          type="checkbox"
          checked={filters.excludeLowConfidence}
          onChange={e => update({ excludeLowConfidence: e.target.checked })}
          className="h-5 w-5 sm:h-4 sm:w-4 text-primary-700 border-edge-input rounded focus:ring-primary-500"
          data-testid="exclude-low-confidence-checkbox"
          data-track="league_filter_low_confidence"
        />
        <span className="ml-2 text-sm text-body">Exclude low confidence ratings</span>
      </label>
      <p className="text-xs text-muted mt-1 ml-7 sm:ml-6">
        Hide players with fewer than 5 games or high uncertainty (σ &gt; 5)
      </p>

      <div className="mt-3 flex items-center justify-between gap-3">
        {!activityReady ? <p className="text-xs text-muted">Loading plays for the activity filters…</p> : <span />}
        <button
          type="button"
          data-track="league_filter_clear"
          data-testid="clear-player-filters"
          onClick={() => onChange({ ...filters, excludeLowConfidence: true, minGames: 0, activeWithinDays: null, gameId: '', search: '' })}
          disabled={!hasActivePanelFilters(filters) && filters.search.trim() === ''}
          className="min-h-[44px] sm:min-h-0 px-3 py-2 sm:py-1 text-sm font-medium text-primary-700 hover:text-primary-900 disabled:text-disabled disabled:cursor-not-allowed"
        >
          Clear filters
        </button>
      </div>
    </div>
  );
};

export default LeaguePlayerFilterPanel;
